import { useTranslation } from '@/core/hooks';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { logger } from '@/core/logger';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useAppStore } from '@/core/stores/appStore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAppointmentLabels } from '../constants/getAppointmentLabels';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  appointmentService,
  extractAppointmentIdFromMutationResponse,
  extractEventIdFromMutationResponse,
  extractUpcomingAppointment,
  formatAppointmentTimeLabel,
  mapSlotTimesFromResponse,
  toAppointmentDateId,
} from '../services/appointmentService';
import type {
  AppointmentFlowStep,
  AppointmentSlotDialogMode,
  AppointmentSlotsLoadState,
  AppointmentSlotTime,
  BookedAppointment,
  UpcomingAppointmentLoadState,
} from '../types/appointment.types';
import { withPastSlotsDisabled } from '../utils/slotAvailability';
import {
  buildSlotDates,
  DEFAULT_SLOT_DATE_COUNT,
  DEFAULT_SLOT_MONTHS,
  formatLongDate,
  formatShortDate,
} from '../utils/slotDates';

function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return;
  const err = error as { status?: number; response?: { status?: number } };
  return err.status ?? err.response?.status;
}

/**
 * Extracts `errorMessage` from the API contract response body.
 * Contract shape: { errorCode: string; errorMessage: string }
 * Returns null if not present so the caller can fall back to stored labels.
 */
function getApiErrorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const err = error as { response?: { data?: { errorMessage?: unknown } }; data?: { errorMessage?: unknown } };
  const raw = err.response?.data?.errorMessage ?? err.data?.errorMessage;
  if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
  return null;
}

/**
 * Per API contract: 409 Conflict means the appointment cannot be processed
 * in its current state (e.g. slot taken by another user).
 * Show SLOT_NO_LONGER_AVAILABLE for this case.
 * All other errors (400, 404, 500) are generic system/Salesforce failures.
 */
function isSlotConflictError(error: unknown): boolean {
  return getErrorStatus(error) === 409;
}

function getSubmitErrorFallback(
  error: unknown,
  slotMode: AppointmentSlotDialogMode,
  labels: ReturnType<typeof getAppointmentLabels>,
): string {
  if (isSlotConflictError(error)) {
    return labels.SLOT_NO_LONGER_AVAILABLE;
  }

  if (slotMode === 'reschedule') {
    return labels.RESCHEDULE_ERROR;
  }

  return labels.BOOK_ERROR;
}

export type {
  AppointmentSlotsLoadState,
  UpcomingAppointmentLoadState
} from '../types/appointment.types';

export interface UseBookAppointmentFlowOptions {
  unitId?: string;
  /**
   * When false, the hook will not call GET /appointments/upcoming.
   * Useful when a screen should not display appointment state from API.
   */
  enableUpcoming?: boolean;
}

function resolveMeetingUrlFromUpcomingDto(
  dto: NonNullable<ReturnType<typeof extractUpcomingAppointment>>,
): string | undefined {
  const candidates = [
    dto.meeting_url,
    dto.meetingUrl,
    dto.join_url,
    dto.joinUrl,
    dto.virtual_meeting_link,
    dto.teams_link,
    dto.location,
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const value = candidate.trim();
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
  }
  return undefined;
}

function mapUpcomingToBooked(
  dto: NonNullable<ReturnType<typeof extractUpcomingAppointment>>,
): BookedAppointment | null {
  const startTime = String(dto.start_time ?? '').trim();
  const dateFromStart = startTime ? toAppointmentDateId(startTime) : '';
  const dateIso = String(
    dateFromStart ||
    dto.date ||
    dto.appointment_date ||
    dto.date_id ||
    '',
  ).trim();
  const timeFromStart = startTime ? formatAppointmentTimeLabel(startTime) : '';
  const timeLabel = String(
    timeFromStart ||
    dto.time ||
    dto.appointment_time ||
    dto.time_id ||
    '',
  ).trim();
  if (!dateIso && !timeLabel) {
    return null;
  }
  const dateId = String(dto.date_id ?? (dateIso || 'unknown-date'));
  const appointmentId = String(dto.appointment_id ?? dto.id ?? '').trim();
  const timeId = String(
    appointmentId ||
    dto.time_id ||
    startTime ||
    timeLabel ||
    'unknown-time',
  );
  const eventId = String(dto.event_id ?? '').trim();
  const mappedUnitId = String(dto.unit_id ?? '').trim();
  const meetingUrl = resolveMeetingUrlFromUpcomingDto(dto);
  return {
    dateId,
    timeId,
    dateLabel: dateIso ? formatShortDate(dateIso) : dateId,
    timeLabel: timeLabel || timeId,
    dateDisplay: dateIso ? formatLongDate(dateIso) : dateId,
    ...(appointmentId ? { appointmentId } : {}),
    ...(eventId ? { eventId } : {}),
    ...(mappedUnitId ? { unitId: mappedUnitId } : {}),
    ...(meetingUrl ? { meetingUrl } : {}),
  };
}

/**
 * Orchestrates book / reschedule / cancel dialog steps for a unit.
 * Listing appointment card is driven solely by GET /appointments/upcoming.
 */
export function useBookAppointmentFlow(
  options: UseBookAppointmentFlowOptions = {},
) {
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const appointmentLabels = getAppointmentLabels(t);
  const unitId = options.unitId;
  const enableUpcoming = options.enableUpcoming ?? true;

  const [step, setStep] = useState<AppointmentFlowStep>('idle');
  const [slotMode, setSlotMode] =
    useState<AppointmentSlotDialogMode>('book');
  /** Listing card — set only from GET /appointments/upcoming response. */
  const [appointment, setAppointment] = useState<BookedAppointment | null>(
    null,
  );
  /** Success dialog payload (not used for listing card). */
  const [confirmedAppointment, setConfirmedAppointment] =
    useState<BookedAppointment | null>(null);
  const [upcomingLoadState, setUpcomingLoadState] =
    useState<UpcomingAppointmentLoadState>(
      enableUpcoming ? 'loading' : 'loaded',
    );
  const [times, setTimes] = useState<AppointmentSlotTime[]>([]);
  const [slotsLoadState, setSlotsLoadState] =
    useState<AppointmentSlotsLoadState>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const slotsRequestIdRef = useRef(0);
  const upcomingRequestIdRef = useRef(0);
  const appointmentBookingStartDate = useAppStore(
    (state) => state.auth.user?.appointmentBookingStartDate,
  );
  const appointmentBookingEndDate = useAppStore(
    (state) => state.auth.user?.appointmentBookingEndDate,
  );

  const slotDates = useMemo(
    () => buildSlotDates(DEFAULT_SLOT_DATE_COUNT, {
      months: DEFAULT_SLOT_MONTHS,
      isRTL,
      todayLabel: appointmentLabels.TODAY,
      tomorrowLabel: appointmentLabels.TOMORROW,
      bookingStartDate: appointmentBookingStartDate,
      bookingEndDate: appointmentBookingEndDate,
    }),
    [
      appointmentBookingEndDate,
      appointmentBookingStartDate,
      appointmentLabels.TODAY,
      appointmentLabels.TOMORROW,
      isRTL,
    ],
  );
  const slotDateValuesKey = useMemo(
    () => slotDates.map((date) => date.value).join('|'),
    [slotDates],
  );

  const loadSlotTimes = useCallback(
    async (date?: string) => {
      const requestId = ++slotsRequestIdRef.current;
      setSlotsLoadState('loading');
      setTimes([]);

      if (!date) {
        setSlotsLoadState('empty');
        return;
      }

      try {
        const response = await appointmentService.getSlots({
          ...(unitId ? { unitId } : {}),
          ...(date ? { date } : {}),
        });
        if (requestId !== slotsRequestIdRef.current) {
          return;
        }

        const mapped = withPastSlotsDisabled(
          mapSlotTimesFromResponse(response),
        );
        if (mapped.length > 0) {
          setTimes(mapped);
          setSlotsLoadState('success');
          return;
        }

        logger.info('useBookAppointmentFlow:Slots empty for date', { date });
        setTimes([]);
        setSlotsLoadState('empty');
      } catch (error) {
        if (requestId !== slotsRequestIdRef.current) {
          return;
        }
        logger.warn('useBookAppointmentFlow:getSlots failed', error);
        setTimes([]);
        setSlotsLoadState('error');
      }
    },
    [unitId],
  );

  const slotWindowKeyRef = useRef(slotDateValuesKey);
  useEffect(() => {
    if (step !== 'slot') {
      slotWindowKeyRef.current = slotDateValuesKey;
      return;
    }
    if (slotWindowKeyRef.current === slotDateValuesKey) {
      return;
    }
    slotWindowKeyRef.current = slotDateValuesKey;
    const firstDate = slotDateValuesKey.split('|')[0] ?? '';
    let date = firstDate;
    if (slotDateValuesKey.length === 0) {
      date = '';
    } else if (slotMode === 'reschedule') {
      date = appointment?.dateId ?? '';
    }
    void loadSlotTimes(date || undefined);
  }, [
    appointment?.dateId,
    loadSlotTimes,
    slotDateValuesKey,
    slotMode,
    step,
  ]);

  /**
   * Single source of truth for the listing appointment card.
   * While loading, UI shows shimmer in the appointment slot (not stale card data).
   */
  const loadUpcoming = useCallback(async (): Promise<BookedAppointment | null> => {
    if (!enableUpcoming) {
      return null;
    }

    const requestId = ++upcomingRequestIdRef.current;
    setUpcomingLoadState('loading');

    try {
      const response = await appointmentService.getUpcoming({
        ...(unitId ? { unitId } : {}),
      });
      if (requestId !== upcomingRequestIdRef.current) {
        return null;
      }

      const dto = extractUpcomingAppointment(response);
      if (!dto) {
        setAppointment(null);
        return null;
      }

      const mapped = mapUpcomingToBooked(dto);
      if (!mapped) {
        setAppointment(null);
        return null;
      }

      setAppointment(mapped);
      return mapped;
    } catch (error) {
      if (requestId !== upcomingRequestIdRef.current) {
        return null;
      }
      logger.warn('useBookAppointmentFlow:getUpcoming failed', error);
      setAppointment(null);
      return null;
    } finally {
      if (requestId === upcomingRequestIdRef.current) {
        setUpcomingLoadState('loaded');
      }
    }
  }, [enableUpcoming, unitId]);

  useEffect(() => {
    if (!enableUpcoming) {
      setUpcomingLoadState('loaded');
      return;
    }
    void loadUpcoming();
  }, [enableUpcoming, loadUpcoming]);

  useEffect(() => {
    if (step === 'confirmedSuccess' && confirmedAppointment == null) {
      setStep('idle');
    }
  }, [step, confirmedAppointment]);

  const openBook = useCallback(() => {
    if (unitId) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.UNIT,
        actionName: ALMETLAA_ACTION.UNIT.bookAppointmentClick(unitId),
        eventType: 'action',
      });
    }
    setSlotMode('book');
    setStep('slot');
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_DIALOG_OPEN,
      eventType: 'action',
    });
    void loadSlotTimes(slotDates[0]?.value);
  }, [loadSlotTimes, slotDates, unitId]);

  const openReschedule = useCallback(() => {
    setSlotMode('reschedule');
    setStep('slot');
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.RESCHEDULE_CLICK,
      eventType: 'action',
    });
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_DIALOG_OPEN,
      eventType: 'action',
    });
    void loadSlotTimes(slotDates.length === 0 ? undefined : appointment?.dateId);
  }, [appointment?.dateId, loadSlotTimes, slotDates.length]);

  const onSlotDateChange = useCallback(
    (dateId: string) => {
      void loadSlotTimes(dateId);
    },
    [loadSlotTimes],
  );

  const openCancel = useCallback(() => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_REQUEST_CLICK,
      eventType: 'action',
    });
    setStep('cancelConfirm');
  }, []);

  const closeSlot = useCallback(() => {
    setStep('idle');
    setSlotsLoadState('idle');
    setTimes([]);
  }, []);

  const submitSlot = useCallback(
    async (payload: { dateId: string; timeId: string }) => {
      const date = slotDates.find((d) => d.id === payload.dateId);
      const time = times.find((t) => t.id === payload.timeId);
      if (!date || !time || isSubmitting || slotsLoadState !== 'success') {
        return;
      }

      setIsSubmitting(true);
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.APPOINTMENT,
        actionName: ALMETLAA_ACTION.APPOINTMENT.SUBMIT_CLICK,
        eventType: 'action',
      });
      try {
        let nextAppointmentId: string | null;
        let nextEventId: string | null;

        if (slotMode === 'reschedule') {
          const eventId = appointment?.eventId?.trim();
          if (!eventId) {
            if (!enableUpcoming) {
              logger.warn(
                'useBookAppointmentFlow:reschedule missing event_id — upcoming refresh disabled',
              );
              setStep('idle');
              return;
            }
            logger.warn(
              'useBookAppointmentFlow:reschedule missing event_id — refresh upcoming and retry',
            );
            await loadUpcoming();
            return;
          }
          const updateResponse = await appointmentService.updateAppointment({
            action_type: 'Rescheduled',
            event_id: eventId,
            appointment_slot_id: time.id,
            reason: 'Rescheduled by customer',
          });
          nextAppointmentId =
            extractAppointmentIdFromMutationResponse(updateResponse) ??
            appointment?.appointmentId ??
            null;
          nextEventId =
            extractEventIdFromMutationResponse(updateResponse) ?? eventId;
        } else {
          const scheduleResponse = await appointmentService.scheduleAppointment({
            appointment_slot_id: time.id,
            ...(unitId ? { unitId } : {}),
          });
          nextAppointmentId = extractAppointmentIdFromMutationResponse(scheduleResponse);
          nextEventId = extractEventIdFromMutationResponse(scheduleResponse);
        }

        const fallbackConfirmed: BookedAppointment = {
          dateId: date.id,
          timeId: time.id,
          dateLabel: date.label,
          timeLabel: time.label,
          dateDisplay: formatLongDate(date.value),
          ...(nextAppointmentId ? { appointmentId: nextAppointmentId } : {}),
          ...(nextEventId ? { eventId: nextEventId } : {}),
          ...(appointment?.unitId || unitId
            ? { unitId: appointment?.unitId ?? unitId }
            : {}),
        };

        const refreshed = enableUpcoming ? await loadUpcoming() : null;
        setConfirmedAppointment(refreshed ?? fallbackConfirmed);
        setStep('confirmedSuccess');
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.APPOINTMENT,
          actionName: ALMETLAA_ACTION.APPOINTMENT.SUCCESS,
          eventType: 'action',
        });
      } catch (error) {
        logger.warn('useBookAppointmentFlow:submitSlot failed', error);
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.APPOINTMENT,
          actionName: ALMETLAA_ACTION.APPOINTMENT.ERROR,
          eventType: 'error',
          properties: {
            p_failedMessage: getApiErrorMessage(error) ?? getSubmitErrorFallback(error, slotMode, appointmentLabels),
          },
        });
        setActionError(
          getApiErrorMessage(error) ??
          getSubmitErrorFallback(error, slotMode, appointmentLabels),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      appointment?.appointmentId,
      appointment?.eventId,
      appointment?.unitId,
      appointmentLabels,
      enableUpcoming,
      isSubmitting,
      loadUpcoming,
      slotDates,
      slotMode,
      slotsLoadState,
      times,
      unitId,
    ],
  );

  const dismissConfirmedSuccess = useCallback(() => {
    setStep('idle');
    setConfirmedAppointment(null);
  }, []);

  const resetDialogs = useCallback(() => {
    setStep('idle');
    setConfirmedAppointment(null);
    setSlotsLoadState('idle');
    setTimes([]);
    setIsSubmitting(false);
  }, []);

  const dismissCancelConfirm = useCallback(() => {
    setStep('idle');
  }, []);

  const resolveCancelEventId = useCallback(async (): Promise<string | null> => {
    if (enableUpcoming === false) {
      logger.warn(
        'useBookAppointmentFlow:cancel missing event_id — upcoming refresh disabled',
      );
      return null;
    }

    try {
      const response = await appointmentService.getUpcoming({
        ...(unitId ? { unitId } : {}),
      });
      const dto = extractUpcomingAppointment(response);
      return dto ? String(dto.event_id ?? '').trim() || null : null;
    } catch (error) {
      logger.warn(
        'useBookAppointmentFlow:cancel upcoming refresh failed',
        error,
      );
      return null;
    }
  }, [enableUpcoming, unitId]);

  const confirmCancel = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      let eventId = appointment?.eventId?.trim();
      if (!eventId) {
        eventId = (await resolveCancelEventId()) ?? undefined;
        if (!eventId) {
          logger.warn(
            'useBookAppointmentFlow:cancel aborted — no appointment event_id',
          );
          setStep('idle');
          setActionError(appointmentLabels.CANCEL_ERROR);
          return;
        }
      }

      await appointmentService.updateAppointment({
        action_type: 'Cancelled',
        event_id: eventId,
        appointment_slot_id: '',
        reason: 'Cancelled by customer',
      });

      if (enableUpcoming) {
        await loadUpcoming();
      } else {
        setAppointment(null);
      }

      setConfirmedAppointment(null);
      setStep('canceledSuccess');
    } catch (error) {
      logger.warn('useBookAppointmentFlow:confirmCancel failed', error);
      setActionError(getApiErrorMessage(error) ?? appointmentLabels.CANCEL_ERROR);
      setStep('idle');
      if (enableUpcoming) {
        await loadUpcoming();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    appointment?.eventId,
    appointmentLabels.CANCEL_ERROR,
    enableUpcoming,
    isSubmitting,
    loadUpcoming,
    resolveCancelEventId,
  ]);

  const dismissCanceledSuccess = useCallback(() => {
    setStep('idle');
  }, []);

  const dismissActionError = useCallback(() => {
    setActionError(null);
  }, []);

  const isFlowActive = step !== 'idle' || isSubmitting;

  return {
    step,
    slotMode,
    appointment,
    confirmedAppointment,
    hasAppointment: appointment != null,
    isFlowActive,
    upcomingLoadState,
    isUpcomingLoading: upcomingLoadState === 'loading',
    isUpcomingResolved: upcomingLoadState === 'loaded',
    slotDates,
    times,
    slotsLoadState,
    isSlotsLoading: slotsLoadState === 'loading',
    isSubmitting,
    openBook,
    openReschedule,
    openCancel,
    closeSlot,
    onSlotDateChange,
    submitSlot,
    dismissConfirmedSuccess,
    resetDialogs,
    dismissCancelConfirm,
    confirmCancel,
    dismissCanceledSuccess,
    actionError,
    dismissActionError,
  };
}
